/**
 * The intake as subtitles (chat direction A): what the screen shows for a conversation. Iris's latest line is the
 * subtitle, a fading tail of three lines sits above it, and under it there is at most one choice. Pure.
 */
import type { TalkMessage } from "./IntakeTalk";

export type SubtitleSize = "large" | "medium" | "small";
export interface SubtitleLine { index: number; role: "maker" | "caprica"; text: string; kind: "line" | "note" | "receipt" }
export interface SubtitleScreen {
  current: { index: number; text: string; size: SubtitleSize; error: boolean } | null;
  tail: SubtitleLine[];
  sheet: { index: number; kind: "cast" | "wardrobe" | "photo_session" | "directions" } | null;
  chips: { index: number; options: string[]; multi: boolean; extras: { id: string; label: string }[] } | null;
  ideasOnBoard: boolean;
  /** A cast offer was answered in words and nobody is chosen yet: the quiet row can bring the portraits back. */
  castAgain: boolean;
}

/** A subtitle never becomes a block of text: longer lines step down in size. */
export function subtitleSize(text: string): SubtitleSize {
  return text.length <= 110 ? "large" : text.length <= 220 ? "medium" : "small";
}

/** A board change or a choice ("✎", "✓") reads as a note; a pasted sheet or an added file as its receipt. */
function kindOf(message: TalkMessage): SubtitleLine["kind"] {
  if (!message.note) return "line";
  return /^[✎✓]/.test(message.text.trim()) ? "note" : "receipt";
}

/** canShow: whether a sheet can be drawn now (a picker needs options, a photo offer an unchosen photo); a sheet that
 * cannot be drawn leaves the line's chips instead. */
export function subtitleView(messages: TalkMessage[], options: { board: boolean; castChosen?: boolean; tail?: number; canShow?: (kind: "cast" | "wardrobe" | "photo_session" | "directions") => boolean }): SubtitleScreen {
  const last = messages.length - 1;
  // Iris speaks last: her line is the subtitle. The maker spoke last: Iris is thinking, their line closes the tail.
  const currentIndex = last >= 0 && messages[last]!.role === "caprica" ? last : -1;
  const message = currentIndex >= 0 ? messages[currentIndex]! : null;
  const tailEnd = currentIndex >= 0 ? currentIndex : messages.length;
  const tail = messages
    .slice(Math.max(0, tailEnd - (options.tail ?? 3)), tailEnd)
    .map((item, offset) => ({ index: Math.max(0, tailEnd - (options.tail ?? 3)) + offset, role: item.role, text: item.text, kind: kindOf(item) }));

  let sheet: SubtitleScreen["sheet"] = null;
  if (message?.offer && !message.picked) sheet = { index: currentIndex, kind: message.offer };
  else if (message?.concepts?.length && !options.board) sheet = { index: currentIndex, kind: "directions" };
  if (sheet && options.canShow && !options.canShow(sheet.kind)) sheet = null;
  const replies = message?.quickReplies;
  const chips = !sheet && replies && !message!.answered
    ? { index: currentIndex, options: replies.options, multi: replies.multi, extras: replies.extras ?? [] }
    : null;

  return {
    current: message ? { index: currentIndex, text: message.text, size: subtitleSize(message.text), error: !!message.error } : null,
    tail,
    sheet,
    chips,
    ideasOnBoard: !!message?.concepts?.length && options.board,
    // The board's Change the cast appears only once there is a cast; until then this is the way back.
    castAgain: !options.castChosen && sheet?.kind !== "cast"
      && messages.some((item) => item.offer === "cast" && !item.picked),
  };
}
